import type { BatchPricesResponse } from "../services/pricingService";

export interface ComponentSelection {
	productId: string | null;
	productName?: string;
	productDescription?: string;
	productDescription2?: string;
	imageUrl?: string;
	availabilityQuantity?: number;
	availabilityStatus?: string;
	calculatedStockQuantity?: number;
	stockStatus?: {
		iconClass?: string;
		description?: string;
	};
	priceDifference?: {
		net: number;
		gross: number;
	};
	price?: {
		net: number;
		gross: number;
	};
}

export interface ComponentSelections {
	[productGroupId: string]: ComponentSelection;
}

export interface DefaultProductInfo {
	price: { net: number; gross: number };
	productId: string;
}

export interface DefaultProductPrices {
	[productGroupId: string]: DefaultProductInfo;
}

const STORAGE_KEY_PREFIX = "configBundle_selections";
const DEFAULT_PRICES_KEY_PREFIX = "configBundle_defaultPrices";

function getStorageKey(configBundleId: string): string {
	return `${STORAGE_KEY_PREFIX}_${configBundleId}`;
}

function getDefaultPricesKey(configBundleId: string): string {
	return `${DEFAULT_PRICES_KEY_PREFIX}_${configBundleId}`;
}

export function loadComponentSelections(configBundleId: string): ComponentSelections {
	try {
		const key = getStorageKey(configBundleId);
		const stored = sessionStorage.getItem(key);
		if (!stored) {
			return {};
		}
		return JSON.parse(stored) as ComponentSelections;
	} catch (error) {
		console.error("Failed to load component selections from sessionStorage:", error);
		return {};
	}
}

export function saveComponentSelections(
	configBundleId: string,
	selections: ComponentSelections
): void {
	try {
		const key = getStorageKey(configBundleId);
		sessionStorage.setItem(key, JSON.stringify(selections));
	} catch (error) {
		console.error("Failed to save component selections to sessionStorage:", error);
	}
}

export function updateComponentSelection(
	configBundleId: string,
	productGroupId: string,
	selection: ComponentSelection
): ComponentSelections {
	const selections = loadComponentSelections(configBundleId);
	selections[productGroupId] = selection;
	saveComponentSelections(configBundleId, selections);
	return selections;
}

export function removeComponentSelection(
	configBundleId: string,
	productGroupId: string
): ComponentSelections {
	const selections = loadComponentSelections(configBundleId);
	delete selections[productGroupId];
	saveComponentSelections(configBundleId, selections);
	return selections;
}

export function clearComponentSelections(configBundleId: string): void {
	try {
		const key = getStorageKey(configBundleId);
		sessionStorage.removeItem(key);
	} catch (error) {
		console.error("Failed to clear component selections from sessionStorage:", error);
	}
}

export function clearAllSelections(): void {
	try {
		const keysToRemove: string[] = [];
		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i);
			if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
				keysToRemove.push(key);
			}
		}
		keysToRemove.forEach((key) => sessionStorage.removeItem(key));
	} catch (error) {
		console.error("Failed to clear all selections from sessionStorage:", error);
	}
}

export function loadDefaultProductPrices(configBundleId: string): DefaultProductPrices {
	try {
		const key = getDefaultPricesKey(configBundleId);
		const stored = sessionStorage.getItem(key);
		if (!stored) {
			return {};
		}
		return JSON.parse(stored) as DefaultProductPrices;
	} catch (error) {
		console.error("Failed to load default prices from sessionStorage:", error);
		return {};
	}
}

export function saveDefaultProductPrices(
	configBundleId: string,
	prices: DefaultProductPrices
): void {
	try {
		const key = getDefaultPricesKey(configBundleId);
		sessionStorage.setItem(key, JSON.stringify(prices));
	} catch (error) {
		console.error("Failed to save default prices to sessionStorage:", error);
	}
}

export function updateDefaultProductPrice(
	configBundleId: string,
	productGroupId: string,
	priceInfo: DefaultProductInfo
): DefaultProductPrices {
	const prices = loadDefaultProductPrices(configBundleId);
	prices[productGroupId] = priceInfo;
	saveDefaultProductPrices(configBundleId, prices);
	return prices;
}

export function clearDefaultProductPrices(configBundleId: string): void {
	try {
		const key = getDefaultPricesKey(configBundleId);
		sessionStorage.removeItem(key);
	} catch (error) {
		console.error("Failed to clear default prices from sessionStorage:", error);
	}
}

export function filterSelectionsToValidGroups(
	selections: ComponentSelections,
	validGroupIds: Set<string>
): ComponentSelections {
	return Object.fromEntries(
		Object.entries(selections).filter(([groupId]) => validGroupIds.has(groupId))
	);
}

export function filterDefaultPricesToValidGroups(
	defaultPrices: DefaultProductPrices,
	validGroupIds: Set<string>
): DefaultProductPrices {
	return Object.fromEntries(
		Object.entries(defaultPrices).filter(([groupId]) => validGroupIds.has(groupId))
	);
}

export function mapBatchResultsToSelections(batchResponse: BatchPricesResponse): ComponentSelections {
	const selections: ComponentSelections = {};

	batchResponse.results.forEach(({ productGroupId, product, isAvailable }) => {
		if (isAvailable && product) {
			selections[productGroupId] = {
				productId: product.id,
				productName: product.title || product.description,
				productDescription: product.description,
				productDescription2: product.description2,
				imageUrl: product.imageUrl,
				availabilityQuantity: product.availabilityQuantity,
				availabilityStatus: product.availabilityStatus,
				calculatedStockQuantity: product.calculatedStockQuantity,
				stockStatus: product.stockStatus,
				priceDifference: product.priceDifference,
				price: product.price,
			};
		}
	});

	if (batchResponse.deselections) {
		batchResponse.deselections.forEach((productGroupId) => {
			selections[productGroupId] = {
				productId: null,
			};
		});
	}

	return selections;
}